import { getIdToken, getSub } from "./CognitoService";
import { fromCognitoIdentityPool } from "@aws-sdk/credential-providers";
import {
  BatchWriteItemCommand,
  DeleteItemCommand,
  DynamoDBClient,
  QueryCommand,
} from "@aws-sdk/client-dynamodb";

const REGION = process.env.REACT_APP_AWS_REGION!;
const IDENTITY_POOL_ID = process.env.REACT_APP_IDENTITY_POOL_ID!;
const TABLE_NAME = process.env.REACT_APP_DDB_TABLE!;

export function createDdbClient() {
  const creds = fromCognitoIdentityPool({
    identityPoolId: IDENTITY_POOL_ID,
    clientConfig: { region: REGION },
    logins: {
      [`cognito-idp.${REGION}.amazonaws.com/${process.env.REACT_APP_COGNITO_USER_POOL_ID}`]:
        async () => await getIdToken(),
    },
  });
  return new DynamoDBClient({ region: REGION, credentials: creds });
}

export async function loadSchedule() {
  // fetch current user sub and fresh ID token
  const tenantId = await getSub();
  const client = createDdbClient();
  const resp = await client.send(
    new QueryCommand({
      TableName: TABLE_NAME,
      KeyConditionExpression: "tenantId = :t and begins_with(SK, :prefix)",
      ExpressionAttributeValues: {
        ":t": { S: tenantId },
        ":prefix": { S: "SCHEDULE#" },
      },
    }),
  );
  return resp.Items || [];
}

export async function saveSchedule(
  weekly: { day: string; open: string; close: string; closed: boolean }[],
) {
  // fetch current user sub and fresh ID token
  const tenantId = await getSub();
  const client = createDdbClient();
  const writes = weekly.map((row) => ({
    PutRequest: {
      Item: {
        tenantId: { S: tenantId },
        SK: { S: `SCHEDULE#${row.day}` },
        open: { S: row.open },
        close: { S: row.close },
        closed: { BOOL: row.closed },
      },
    },
  }));
  await client.send(
    new BatchWriteItemCommand({ RequestItems: { [TABLE_NAME]: writes } }),
  );
}

/**
 * Load special hours (Overrides) for a specific calendar week.
 */
export async function loadOverrideSchedule(weekNum: number, weekYear: number) {
  // fetch current user sub and fresh ID token
  const tenantId = await getSub();
  const client = createDdbClient();
  const prefix = `OVERRIDE#${weekYear}-KW${weekNum}#`;
  const resp = await client.send(
    new QueryCommand({
      TableName: TABLE_NAME,
      KeyConditionExpression: "tenantId = :t and begins_with(SK, :p)",
      ExpressionAttributeValues: {
        ":t": { S: tenantId },
        ":p": { S: prefix },
      },
    }),
  );
  return resp.Items || [];
}

/**
 * Save special hours (Overrides) for a specific calendar week.
 */
export async function saveOverrideSchedule(
  weekNum: number,
  weekYear: number,
  weekly: {
    day: string;
    date: string;
    open: string;
    close: string;
    closed: boolean;
  }[],
) {
  // fetch current user sub and fresh ID token
  const tenantId = await getSub();
  const client = createDdbClient();
  const prefix = `OVERRIDE#${weekYear}-KW${weekNum}#`;
  const writes = weekly.map((row) => ({
    PutRequest: {
      Item: {
        tenantId: { S: tenantId },
        SK: { S: `${prefix}${row.day}` },
        date: { S: row.date },
        open: { S: row.open },
        close: { S: row.close },
        closed: { BOOL: row.closed },
      },
    },
  }));
  await client.send(
    new BatchWriteItemCommand({ RequestItems: { [TABLE_NAME]: writes } }),
  );
}

export interface StaffSchedule {
  name: string;
  calendarId: string;
  schedule: { day: string; open: string; close: string; closed: boolean }[];
}

/**
 * Load all staff members along with their working hours in a single item.
 */
export async function loadStaffWithSchedules(): Promise<StaffSchedule[]> {
  const tenantId = await getSub();
  const client = createDdbClient();
  const resp = await client.send(
    new QueryCommand({
      TableName: TABLE_NAME,
      KeyConditionExpression: "tenantId = :t and begins_with(SK, :p)",
      ExpressionAttributeValues: {
        ":t": { S: tenantId },
        ":p": { S: "STAFF#" },
      },
    }),
  );
  return (resp.Items || []).map((item) => {
    const name = item.SK!.S!.replace(/^STAFF#/, "");
    const calendarId = item.calendarId?.S! ?? "";
    const scheduleAttr = item.schedule?.L || [];
    const schedule = scheduleAttr.map((av) => {
      const m = av.M!;
      return {
        day: m.day.S!,
        open: m.open.S!,
        close: m.close.S!,
        closed: m.closed.BOOL!,
      };
    });
    return { name, calendarId, schedule };
  });
}

/**
 * Save multiple staff members with their working hours in one batch.
 */
export async function saveStaffWithSchedules(staffSchedules: StaffSchedule[]) {
  const tenantId = await getSub();
  const client = createDdbClient();
  const writes = staffSchedules.map(({ name, calendarId, schedule }) => ({
    PutRequest: {
      Item: {
        tenantId: { S: tenantId },
        SK: { S: `STAFF#${name}` },
        calendarId: { S: calendarId },
        schedule: {
          L: schedule.map(({ day, open, close, closed }) => ({
            M: {
              day: { S: day },
              open: { S: open },
              close: { S: close },
              closed: { BOOL: closed },
            },
          })),
        },
      },
    },
  }));
  await client.send(
    new BatchWriteItemCommand({ RequestItems: { [TABLE_NAME]: writes } }),
  );
}

/**
 * Delete a staff member and their schedule.
 */
export async function deleteStaff(name: string) {
  const tenantId = await getSub();
  const client = createDdbClient();
  await client.send(
    new DeleteItemCommand({
      TableName: TABLE_NAME,
      Key: {
        tenantId: { S: tenantId },
        SK: { S: `STAFF#${name}` },
      },
    }),
  );
}
